import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Button, Card, StatusIndicator, Input, FormGroup } from '../../design-system/components';
import { cn } from '../../design-system/utils/cn';

const FRPSettings = () => {
  const { apiRequest } = useAuth();
  const [frpStatus, setFrpStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [configuring, setConfiguring] = useState(false);
  const [error, setError] = useState(null);
  const [showConfigForm, setShowConfigForm] = useState(false);
  
  const [formData, setFormData] = useState({
    hubName: '',
    token: ''
  });

  useEffect(() => {
    fetchFRPStatus();
  }, []);

  const fetchFRPStatus = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const {response,data} = await apiRequest('/api/frp/status');
      // const data = await response.json();
      
      if (data.success) {
        setFrpStatus(data.data);
        
        // Pre-populate form with existing configuration
        if (data.data.configuration && data.data.hasConfiguration) {
          setFormData(prev => ({
            ...prev,
            hubName: data.data.configuration.hubName || ''
          }));
        }
      } else {
        setError(data.error || 'Failed to fetch FRP status');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleConfigure = async (e) => {
    e.preventDefault();
    
    try {
      setConfiguring(true);
      setError(null);
      
      const {response,data} = await apiRequest('/api/frp/register', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });
      
      // const data = await response.json();
      
      if (data.success) {
        setShowConfigForm(false);
        await fetchFRPStatus(); // Refresh status
      } else {
        setError(data.error || 'Failed to register hub for remote access');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    } finally {
      setConfiguring(false);
    }
  };

  const handleServiceControl = async (action) => {
    try {
      setError(null);
      
      const {response ,data} = await apiRequest(`/api/frp/${action}`, {
        method: 'POST'
      });
      
      // const data = await response.json();
      
      if (data.success) {
        await fetchFRPStatus(); // Refresh status
      } else {
        setError(data.error || `Failed to ${action} FRP service`);
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  };

  const handleRemoveConfiguration = async () => {
    if (!window.confirm('Are you sure you want to remove the FRP configuration? This will stop remote access.')) {
      return;
    }
    
    try {
      setError(null);
      
      const {response,data} = await apiRequest('/api/frp/configuration', {
        method: 'DELETE'
      });
      
      // const data = await response.json();
      
      if (data.success) {
        setFormData({
          hubName: '',
          token: ''
        });
        await fetchFRPStatus(); // Refresh status
      } else {
        setError(data.error || 'Failed to remove FRP configuration');
      }
    } catch (err) {
      setError('Network error: ' + err.message);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-4xl">
        <h3 className="text-xl font-semibold mb-6">Remote Access (FRP)</h3>
        <div className="text-center py-8 text-gray-500">Loading FRP status...</div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl">
      <h3 className="text-xl font-semibold mb-2">Remote Access (FRP)</h3>
      <p className="text-gray-600 mb-6">
        Configure Fast Reverse Proxy (FRP) for secure remote access to your hub from anywhere.
      </p>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md mb-6">
          <strong>Error:</strong> {error}
        </div>
      )}

      {frpStatus && (
        <Card className="mb-6">
          <h4 className="text-lg font-medium mb-4">Current Status</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Service Status:</span>
              <StatusIndicator 
                status={frpStatus.service.isActive ? 'online' : 'offline'}
                text={frpStatus.service.status}
                size="sm"
              />
            </div>
            <div className="flex justify-between items-center">
              <span className="font-medium text-gray-700">Configuration:</span>
              <StatusIndicator 
                status={frpStatus.hasConfiguration ? 'online' : 'offline'}
                text={frpStatus.hasConfiguration ? 'Configured' : 'Not Configured'}
                size="sm"
              />
            </div>
            {frpStatus.configuration && frpStatus.hasConfiguration && (
              <>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Server:</span>
                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                    {frpStatus.configuration.serverAddr}:{frpStatus.configuration.serverPort}
                  </span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Remote Port:</span>
                  <span className="font-mono text-sm bg-gray-100 px-2 py-1 rounded">
                    {frpStatus.configuration.remotePort}
                  </span>
                </div>
              </>
            )}
          </div>
        </Card>
      )}

      <div className="mb-6">
        {!frpStatus?.hasConfiguration ? (
          <Button 
            variant="primary"
            onClick={() => setShowConfigForm(true)}
          >
            Configure Remote Access
          </Button>
        ) : (
          <div className="flex flex-wrap gap-3">
            {!frpStatus.service.isActive ? (
              <Button 
                variant="primary"
                onClick={() => handleServiceControl('start')}
              >
                Start Service
              </Button>
            ) : (
              <Button 
                variant="secondary"
                onClick={() => handleServiceControl('stop')}
              >
                Stop Service
              </Button>
            )}
            
            <Button 
              variant="secondary"
              onClick={() => handleServiceControl('restart')}
              disabled={!frpStatus.hasConfiguration}
            >
              Restart Service
            </Button>
            
            <Button 
              variant="primary"
              onClick={() => setShowConfigForm(true)}
            >
              Reconfigure
            </Button>
            
            <Button 
              variant="danger"
              onClick={handleRemoveConfiguration}
            >
              Remove Configuration
            </Button>
          </div>
        )}
      </div>

      {showConfigForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <Card className="w-full max-w-md max-h-screen overflow-y-auto">
            <h4 className="text-lg font-semibold mb-2">Enable Remote Access</h4>
            <p className="text-gray-600 text-sm mb-6">
              Register this hub with the central server to enable secure remote access.
            </p>
            <form onSubmit={handleConfigure} className="space-y-4">
              <FormGroup
                label="Hub Name"
                description="Unique name for this hub (lowercase, use hyphens instead of spaces)"
              >
                <Input
                  type="text"
                  name="hubName"
                  value={formData.hubName}
                  onChange={handleInputChange}
                  placeholder="living-room-hub"
                  required
                />
              </FormGroup>

              <FormGroup
                label="Authentication Token"
                description="Secret token provided by your administrator"
              >
                <Input
                  type="password"
                  name="token"
                  value={formData.token}
                  onChange={handleInputChange}
                  placeholder="Your authentication token"
                  required
                />
              </FormGroup>

              <div className="flex gap-3 justify-end pt-4">
                <Button 
                  type="button"
                  variant="secondary"
                  onClick={() => setShowConfigForm(false)}
                  disabled={configuring}
                >
                  Cancel
                </Button>
                <Button 
                  type="submit"
                  variant="primary"
                  disabled={configuring}
                  loading={configuring}
                >
                  {configuring ? 'Registering...' : 'Register & Enable'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      <Card className="mt-8">
        <h4 className="text-lg font-medium mb-4">About Remote Access</h4>
        <ul className="space-y-2 text-gray-700">
          <li>• FRP (Fast Reverse Proxy) enables secure remote access to your hub</li>
          <li>• You need a FRP server running on a public server/VPS</li>
          <li>• All traffic is encrypted and authenticated</li>
          <li>• Multiple ports are configured for web interface, API, Zigbee, and cameras</li>
          <li>• Configuration file location: <code className="bg-gray-100 px-2 py-1 rounded text-sm font-mono">/opt/smart-hub/frp/config/frpc.toml</code></li>
        </ul>
      </Card>
    </div>
  );
};

export default FRPSettings;